/**
 * Passive network throughput probe.
 *
 * The previous speed indicator (mixins/speedTestMixin.js, removed along with
 * this file's arrival) worked by downloading a 100 MB file from a third-party
 * speed-test host — an explicit, manually triggered test that added ~10
 * seconds of saturated downlink on a machine that is usually busy pulling
 * DICOM. That cost is why its footer badge ended up commented out and never
 * came back.
 *
 * This measures the same thing without issuing a single extra request. Every
 * API call the app already makes is an in-flight transfer with a known byte
 * count and a known duration, so throughput falls out of traffic that was
 * going to happen anyway:
 *
 *   - Downloads are read from Resource Timing via a PerformanceObserver, which
 *     the browser populates for every response whether or not anyone looks.
 *     `transferSize` is bytes as they crossed the wire — compressed — and
 *     responseStart→responseEnd is the matching window. That pairing is the
 *     whole point: counting bytes out of a stream reader instead would count
 *     them DECODED, and since this app's JSON is served gzipped, that reported
 *     a link several times faster than it is.
 *   - The window excludes DNS, TLS and server think time but INCLUDES the TCP
 *     slow-start ramp. Skipping the ramp measures peak steady-state rate, which
 *     reads above the line rate and disagrees with any consumer speed test;
 *     including it measures what the transfer actually averaged.
 *   - Readings are aggregated across a batch of transfers, never taken from a
 *     single small one. Below a few hundred KB the timing window is a handful
 *     of rounded milliseconds and the payload was largely buffered before the
 *     clock started, so one response in isolation can report several times the
 *     line rate. Bytes are summed and divided by the merged busy window.
 *   - A request interceptor attaches an upload progress listener to the XHR
 *     axios is about to open. Uploads have no transferSize equivalent, so this
 *     is the only way to see them. XMLHttpRequest fires these events
 *     regardless; the only new cost is one closure per request.
 *   - At install time the resource-timing buffer is drained once, so the badge
 *     has a reading from the moment it first renders. Without this the first
 *     number appears only after the app happens to fetch something large, which
 *     on a study-list screen can be a long wait. The page load itself is the
 *     best sample available — several MB of bundle over a known window — and it
 *     has already happened by the time any component mounts.
 *
 * Samples below MIN_SAMPLE_BYTES are discarded. A 2 kB JSON response is
 * latency-dominated — it says nothing about bandwidth and would report wild
 * numbers — so the badge stays on its previous reading rather than flickering.
 *
 * Consequence worth knowing: uplink is only observable when something is
 * actually uploaded. On a session that only reads study lists the upload
 * figure stays blank, because inventing one would mean sending traffic, which
 * is the exact cost this module exists to avoid.
 *
 * The one exception is runManualSpeedTest() at the bottom of this file, which
 * does download filler on purpose. It is wired to a button and nothing else:
 * no timer, no page-load hook, no automatic retry. Everything above it remains
 * passive.
 */

import Axios from "axios";

// Below this, transfer time is dominated by round-trip latency rather than
// bandwidth, so the division produces noise.
const MIN_SAMPLE_BYTES = 16 * 1024;

// Guards against a division by a near-zero duration when several progress
// events land inside the same millisecond.
const MIN_SAMPLE_MS = 25;

// Exponential smoothing. Low enough that one unusually slow response does not
// dominate the badge, high enough that a genuine drop shows within a few calls.
const EWMA_ALPHA = 0.3;

// The badge does not need to repaint per progress event.
const PUBLISH_THROTTLE_MS = 1000;

// A reading older than this describes a network condition that may no longer
// exist, so the UI renders it as stale instead of as current fact.
export const SAMPLE_TTL_MS = 2 * 60 * 1000;

const state = {
  downMbps: null,
  upMbps: null,
  downAt: 0,
  upAt: 0,
};

const listeners = new Set();
let publishTimer = null;
let installed = false;

function snapshot() {
  return {
    downMbps: state.downMbps,
    upMbps: state.upMbps,
    downAt: state.downAt,
    upAt: state.upAt,
  };
}

export function getSnapshot() {
  return snapshot();
}

/**
 * Subscribe to smoothed readings. Returns the unsubscribe function; callers
 * must invoke it on destroy or the closure keeps the component alive.
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  const current = snapshot();
  listeners.forEach((fn) => {
    try {
      fn(current);
    } catch (e) {
      // A broken subscriber must not stop the others from updating.
    }
  });
}

/**
 * `immediate` is used for the first reading in a direction: the throttle exists
 * to stop the badge repainting per progress event, but applying it to the very
 * first sample just means the badge sits on "--" for an extra second after it
 * already knows the answer.
 */
function publish(immediate) {
  if (immediate) {
    clearTimeout(publishTimer);
    publishTimer = null;
    emit();
    return;
  }
  if (publishTimer !== null) return;
  publishTimer = setTimeout(() => {
    publishTimer = null;
    emit();
  }, PUBLISH_THROTTLE_MS);
}

/**
 * Fold one observation into the smoothed value for its direction.
 * `bytes` is wire bytes, `ms` is the transfer window in milliseconds.
 */
export function recordSample(direction, bytes, ms) {
  if (!(bytes >= MIN_SAMPLE_BYTES) || !(ms >= MIN_SAMPLE_MS)) return false;

  const mbps = (bytes * 8) / (ms / 1000) / 1e6;
  if (!isFinite(mbps) || mbps <= 0) return false;

  const key = direction === "up" ? "upMbps" : "downMbps";
  const stamp = direction === "up" ? "upAt" : "downAt";
  const previous = state[key];

  state[key] =
    previous === null ? mbps : previous + EWMA_ALPHA * (mbps - previous);
  state[stamp] = Date.now();

  publish(previous === null);
  return true;
}

/**
 * Per-request accumulator. Only the first and last progress events matter:
 * everything between them is already covered by the byte delta.
 */
function newLeg() {
  return { firstAt: 0, firstLoaded: 0, lastAt: 0, lastLoaded: 0, ticks: 0 };
}

function observe(leg, event) {
  const now =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();
  const loaded = event && typeof event.loaded === "number" ? event.loaded : 0;

  if (leg.ticks === 0) {
    // Bytes already delivered when the first event fires arrived before we
    // had a clock running, so they are excluded from the sample rather than
    // credited to a zero-length window.
    leg.firstAt = now;
    leg.firstLoaded = loaded;
  }
  leg.lastAt = now;
  leg.lastLoaded = loaded;
  leg.ticks += 1;
}

function commit(leg, direction) {
  if (!leg || leg.ticks < 2) return;
  recordSample(direction, leg.lastLoaded - leg.firstLoaded, leg.lastAt - leg.firstAt);
}

function wrapProgress(original, leg) {
  return (event) => {
    observe(leg, event);
    // Feature handlers (the exam-upload progress bars) keep working; the probe
    // never replaces a caller's own listener.
    if (typeof original === "function") {
      try {
        original(event);
      } catch (e) {
        // Swallow: a throwing feature handler must not abort the transfer.
      }
    }
  };
}

function commitConfig(config) {
  const probe = config && config.__netProbe;
  if (!probe) return;
  // Upload only. Downloads are measured from Resource Timing instead, because
  // progress events count decoded bytes and every JSON response on this app is
  // served gzipped — see the note on wireMeasurementFor(). Request bodies are
  // multipart scan data that is already compressed, so `loaded` is a fair
  // count for the direction that has no transferSize equivalent.
  commit(probe.up, "up");
  // Cleared so a retried config cannot re-commit a finished transfer.
  config.__netProbe = null;
}

/**
 * Total time during which at least one transfer was in flight.
 *
 * Page-load resources overlap heavily — the browser opens several connections
 * at once — so adding up per-entry durations counts the same wall-clock
 * milliseconds repeatedly and reports a fraction of the real link speed.
 * Merging the intervals first means the division is total bytes over the time
 * the wire was actually busy, and it drops the idle gaps between bursts
 * instead of charging them to the connection.
 */
function activeWindowMs(intervals) {
  if (intervals.length === 0) return 0;
  intervals.sort((a, b) => a.start - b.start);

  let total = 0;
  let start = intervals[0].start;
  let end = intervals[0].end;

  for (let i = 1; i < intervals.length; i += 1) {
    const next = intervals[i];
    if (next.start <= end) {
      if (next.end > end) end = next.end;
    } else {
      total += end - start;
      start = next.start;
      end = next.end;
    }
  }

  return total + (end - start);
}

/**
 * One-shot drain of everything the browser downloaded before this module was
 * loaded — bundles, stylesheets, fonts, logos. Folded in as a single aggregate
 * sample rather than one per file, because these transfers ran concurrently.
 *
 * Entries served from cache report transferSize 0 and are skipped: a cache hit
 * measures the disk, not the network.
 */
export function seedFromResourceTiming() {
  if (
    typeof performance === "undefined" ||
    typeof performance.getEntriesByType !== "function"
  ) {
    return false;
  }

  let entries;
  try {
    entries = performance.getEntriesByType("resource") || [];
  } catch (e) {
    return false;
  }

  let bytes = 0;
  const intervals = [];

  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    const size = entry.transferSize || 0;
    const start = entry.responseStart || 0;
    const end = entry.responseEnd || 0;

    if (size <= 0 || start <= 0 || end <= start) continue;

    bytes += size;
    intervals.push({ start, end });
  }

  return recordSample("down", bytes, activeWindowMs(intervals));
}

/**
 * PerformanceObserver leg: the sole source of download readings.
 *
 * XHR and fetch entries are deliberately INCLUDED. They used to be skipped to
 * avoid double-counting against the axios progress-event leg, but that leg
 * measured decoded bytes and over-reported on gzipped responses, so it was
 * removed and this is now the only counter — nothing can be counted twice.
 *
 * transferSize is zero for cache hits and for cross-origin responses without
 * Timing-Allow-Origin; those entries are skipped, since there is nothing to
 * measure and no request is made to compensate.
 */
function installResourceObserver() {
  if (typeof PerformanceObserver === "undefined") return;

  try {
    const observer = new PerformanceObserver((list) => {
      // Aggregated, never per-entry. Dividing one small response by its own
      // window is the single biggest source of nonsense readings: measured
      // against a real endpoint, a 32 KB response arriving in 1.8 ms reports
      // 147 Mb/s and a 64 KB one reports 405 Mb/s, because at that size the
      // window is a couple of rounded milliseconds and the payload was already
      // sitting in a buffer. Summing the batch and dividing by the merged busy
      // window — the same arithmetic seedFromResourceTiming() uses — averages
      // those outliers out instead of letting one of them set the badge.
      const entries = list.getEntries();
      let bytes = 0;
      const intervals = [];

      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        const size = entry.transferSize || 0;
        const start = entry.responseStart || 0;
        const end = entry.responseEnd || 0;
        if (size <= 0 || start <= 0 || end <= start) continue;

        bytes += size;
        intervals.push({ start, end });
      }

      recordSample("down", bytes, activeWindowMs(intervals));
    });
    observer.observe({ type: "resource", buffered: false });
  } catch (e) {
    // Older browsers reject the `type` form; the seed reading still stands.
  }
}

/**
 * Idempotent. Safe to call from every view that shows the badge.
 *
 * Call this at bootstrap rather than from the component that renders the
 * reading: Vue creates a parent before its children, so a probe installed in
 * the footer's created() hook misses every request the surrounding view has
 * already dispatched from its own created() — which on this app is the whole
 * initial study-list load, the largest transfer of the session.
 */
export function installNetworkSpeedProbe(client = Axios) {
  if (installed) return;
  installed = true;

  // Before the interceptors, so a reading exists even if no API call of
  // measurable size happens for a while.
  seedFromResourceTiming();

  client.interceptors.request.use((config) => {
    if (config.__netProbe) return config;

    const probe = { up: newLeg() };
    config.__netProbe = probe;
    // No onDownloadProgress wrapper: the download side is measured from
    // Resource Timing, which reports wire bytes rather than decoded bytes.
    config.onUploadProgress = wrapProgress(config.onUploadProgress, probe.up);
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      commitConfig(response.config);
      return response;
    },
    (error) => {
      // A failed request still transferred real bytes up to the point it died,
      // and that measurement is as valid as any other.
      commitConfig(error && error.config);
      return Promise.reject(error);
    }
  );

  installResourceObserver();
}

// ---------------------------------------------------------------------------
// Manual test
//
// Everything above measures traffic that was going to happen anyway. This does
// the opposite: it downloads filler purely to time it. That is a deliberate
// exception, not a relaxation of the rule — it runs only when a user clicks
// the button, never on a timer, never on load, never on retry. The passive
// probe stays the source of the badge's continuous reading.
//
// 32 MB. The earlier 8 MB was too small to be representative: on a 100 Mbit
// link it is over in well under a second, so TCP slow-start and any single
// scheduling hiccup make up a large fraction of the sample and the result
// swings. A 32 MB transfer runs long enough to settle into steady state.
// Slow links do not pay the full cost — the in-loop timeout stops the download
// early and measures what did arrive.
// ---------------------------------------------------------------------------

export const MANUAL_TEST_BYTES = 32 * 1024 * 1024;
const MANUAL_TEST_TIMEOUT_MS = 20000;

let manualController = null;

export function isManualTestRunning() {
  return manualController !== null;
}

export function cancelManualSpeedTest() {
  if (manualController) manualController.abort();
}

/**
 * Overwrites a direction outright instead of smoothing into it. A manual test
 * is a far better measurement than any single passive sample, so blending it
 * with the EWMA history would only drag a deliberate reading toward stale ones.
 */
function applyReading(direction, mbps) {
  const key = direction === "up" ? "upMbps" : "downMbps";
  const stamp = direction === "up" ? "upAt" : "downAt";

  state[key] = mbps;
  state[stamp] = Date.now();
  publish(true);
}

function clockNow() {
  return typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();
}

/**
 * Authoritative wire measurement for a URL that has finished downloading.
 *
 * Counting bytes as they come out of a stream reader measures the DECOMPRESSED
 * body. Anything the server gzipped — JSON especially, but also the zero-filled
 * payload a speed-test endpoint returns — is several times larger after
 * decoding than it was on the wire, so dividing decoded bytes by wire time
 * reports a link far faster than it is.
 *
 * `transferSize` is what actually crossed the network, compression included,
 * and responseStart→responseEnd is the matching window. Returns null when the
 * entry is missing or the timings are zeroed (cross-origin without
 * Timing-Allow-Origin), in which case the caller keeps its own numbers.
 */
export function wireMeasurementFor(url) {
  if (
    typeof performance === "undefined" ||
    typeof performance.getEntriesByName !== "function"
  ) {
    return null;
  }

  let entries;
  try {
    entries = performance.getEntriesByName(url) || [];
  } catch (e) {
    return null;
  }

  const entry = entries[entries.length - 1];
  if (!entry) return null;

  const bytes = entry.transferSize || 0;
  const ms = (entry.responseEnd || 0) - (entry.responseStart || 0);
  if (bytes <= 0 || !(entry.responseStart > 0) || !(ms > 0)) return null;

  return { bytes, ms };
}

/**
 * Download filler and time it. Resolves to { mbps, bytes, ms }.
 *
 * `options.url` is required and must accept ?bytes=N — the caller supplies it
 * rather than this module reading config directly, which keeps the endpoint a
 * single value owned by config.js and keeps this file free of import-time
 * browser dependencies.
 *
 * Rejects if the endpoint is unreachable, returns an error status, or is
 * cancelled — the caller is expected to surface that rather than fall back to
 * a guess. Only one test runs at a time.
 */
export async function runManualSpeedTest(options = {}) {
  if (manualController) {
    throw new Error("A speed test is already running.");
  }
  if (typeof fetch !== "function") {
    throw new Error("This browser cannot run a speed test.");
  }
  if (!options.url) {
    throw new Error("Speed test endpoint is not configured.");
  }

  const bytes = options.bytes || MANUAL_TEST_BYTES;
  const timeoutMs = options.timeoutMs || MANUAL_TEST_TIMEOUT_MS;
  const onProgress =
    typeof options.onProgress === "function" ? options.onProgress : null;

  const url = `${options.url}?bytes=${bytes}`;
  const controller = new AbortController();
  manualController = controller;

  // Backstop: covers a connection that opens and then stalls before delivering
  // a first byte, which the in-loop check below would never see.
  const timer = setTimeout(() => controller.abort(), timeoutMs * 2);

  let received = 0;
  let startedAt = 0;
  let endedAt = 0;
  let aborted = false;

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`Speed test endpoint returned ${response.status}.`);
    }
    if (!response.body || typeof response.body.getReader !== "function") {
      throw new Error("This browser cannot stream the speed test response.");
    }

    const reader = response.body.getReader();

    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;

      const size = chunk.value ? chunk.value.length : 0;

      if (startedAt === 0) {
        // Clock starts when the first byte lands, so DNS, TLS and server think
        // time stay out of the measurement. The first chunk's bytes ARE counted:
        // dropping them used to discard the whole TCP slow-start ramp, which
        // measured peak steady-state rate rather than what the transfer actually
        // averaged, and reported well above the line rate as a result.
        startedAt = clockNow();
      }

      received += size;
      endedAt = clockNow();

      if (onProgress) onProgress(Math.min(1, received / bytes));

      if (endedAt - startedAt >= timeoutMs) {
        // Enough of a sample on a slow link; stop rather than make the user
        // wait out the whole transfer.
        aborted = true;
        controller.abort();
        break;
      }
    }
  } catch (error) {
    const wasCancelled = error && error.name === "AbortError";
    if (!wasCancelled) throw error;
    if (!aborted) throw new Error("Speed test cancelled.");
  } finally {
    clearTimeout(timer);
    manualController = null;
  }

  // Prefer the browser's own accounting when it is available: it reports wire
  // bytes rather than decoded bytes, so a compressed payload cannot inflate the
  // result. The streamed figures are the fallback for a cancelled transfer or
  // an endpoint that does not expose timing.
  const wire = aborted ? null : wireMeasurementFor(url);
  const measuredBytes = wire ? wire.bytes : received;
  const ms = wire ? wire.ms : endedAt - startedAt;

  if (measuredBytes < MIN_SAMPLE_BYTES || ms < MIN_SAMPLE_MS) {
    throw new Error("Speed test did not transfer enough data to measure.");
  }

  const mbps = (measuredBytes * 8) / (ms / 1000) / 1e6;
  if (!isFinite(mbps) || mbps <= 0) {
    throw new Error("Speed test produced an unusable measurement.");
  }

  applyReading("down", mbps);
  return { mbps, bytes: measuredBytes, ms };
}

// Test seam: unit tests need a clean module between cases.
export function __resetForTests() {
  state.downMbps = null;
  state.upMbps = null;
  state.downAt = 0;
  state.upAt = 0;
  listeners.clear();
  clearTimeout(publishTimer);
  publishTimer = null;
  installed = false;
  manualController = null;
}
